import React from "react";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Theme } from "../../theme";

interface ArticleProps {
	date: string;
	title: string;
	description: string;
	link: string;
}

// アニメーション
const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const ArticleContainer = styled.div<{ theme: Theme }>`
	padding: ${props => props.theme.density.cardPadding};
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: 1rem;
	transition: border-color 0.2s ease, transform 0.2s ease;
	animation: ${fadeIn} 0.5s ease-out;
	box-shadow: ${props => props.theme.shadows.base};

	a {
		text-decoration: none;
	}

	&:hover {
		transform: translateY(-2px);
		border-color: ${props => props.theme.colors.primary.main};
	}

	@media (max-width: 768px) {
		padding: ${props => props.theme.density.cardPaddingMobile};
	}
`;

const ArticleDate = styled.div<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-size: 0.875rem;
	font-weight: 500;
	margin-bottom: 0.75rem;
`;

const ArticleContent = styled.div`
	flex: 1;
`;

const ArticleTitle = styled.h3<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.primary};
	font-size: ${props => props.theme.typography.roles.title.fontSize};
	font-weight: ${props => props.theme.typography.roles.title.fontWeight};
	margin: 0 0 0.75rem 0;
	line-height: 1.4;
	transition: color 0.3s ease;

	${ArticleContainer}:hover & {
		color: ${props => props.theme.colors.primary.main};
	}

	@media (max-width: 768px) {
		font-size: 1.1rem;
	}
`;

const ArticleDescription = styled.p<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	font-size: ${props => props.theme.typography.roles.bodySmall.fontSize};
	line-height: ${props => props.theme.typography.roles.bodySmall.lineHeight};
	margin: 0 0 1rem 0;

	@media (max-width: 768px) {
		font-size: 0.9rem;
	}
`;

const ArticleLink = styled.div<{ theme: Theme }>`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	color: ${props => props.theme.colors.primary.main};
	font-weight: 600;
	font-size: 0.9rem;
	transition: all 0.2s ease;

	svg {
		transition: transform 0.2s ease;
	}

	${ArticleContainer}:hover & {
		transform: translateX(4px);

		svg {
			transform: translateX(2px);
		}
	}
`;

const Article: React.FC<ArticleProps> = (props) => {
	const { date, title, description, link } = props;
	const { theme } = useTheme();
	const { t } = useLanguage();

	return (
		<ArticleContainer theme={theme}>
			<Link to={link}>
				<ArticleContent>
					<ArticleTitle theme={theme}>{title}</ArticleTitle>
					<ArticleDescription theme={theme}>{description}</ArticleDescription>
					<ArticleDate theme={theme}>{date}</ArticleDate>
					<ArticleLink theme={theme}>
						{t.outputs.readMore}
						<FontAwesomeIcon style={{ fontSize: "10px" }} icon={faChevronRight} />
					</ArticleLink>
				</ArticleContent>
			</Link>
		</ArticleContainer>
	);
};

export default Article;
